import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import PriceTable from '@/components/feature/PriceTable';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { REASSURANCE_ITEMS } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const PRICING_ROWS = [
  { service: 'Traitement + barrière périmétrique maison', price: '120 € – 280 €' },
  { service: 'Forfait 2 passages/an (maison exposée)', price: 'Sur devis' },
  { service: 'Combiné scolopendres + ravets (mêmes zones traitées)', price: 'Tarif groupé sur devis' },
];

const STEPS = [
  {
    number: '1',
    icon: 'ri-search-eye-line',
    title: 'Inspection des zones refuges',
    description: 'Pourtour de la maison, tas de bois et pierres, regards, vides sanitaires, dessous de terrasses, gouttières, salles d\'eau.',
  },
  {
    number: '2',
    icon: 'ri-contrast-drop-line',
    title: 'Traitement des refuges et points d\'entrée',
    description: 'Produit professionnel rémanent appliqué aux endroits stratégiques — seuils, soupiraux, contours de portes et fenêtres, joints de dallage, regards.',
  },
  {
    number: '3',
    icon: 'ri-shield-flash-line',
    title: 'Barrière périmétrique',
    description: 'Traitement en ceinture autour de l\'habitation, qui neutralise les scolopendres (et au passage cafards et fourmis) avant qu\'elles n\'entrent.',
  },
  {
    number: '4',
    icon: 'ri-lightbulb-line',
    title: 'Conseils de prévention durable',
    description: 'Gestion des abris extérieurs, moustiquaires de soupiraux, bas de portes, chasse à l\'humidité intérieure.',
  },
];

const FAQ_ITEMS = [
  {
    question: 'La scolopendre de Guadeloupe est-elle dangereuse ?',
    answer: 'Sa morsure est très douloureuse et peut nécessiter un avis médical (enfants, allergiques), mais elle est rarement grave chez l\'adulte en bonne santé. C\'est surtout un animal qu\'on ne veut PAS chez soi — et ça se prévient.',
  },
  {
    question: 'Pourquoi j\'en trouve dans ma salle de bain ?',
    answer: 'Elle cherche l\'eau et la fraîcheur, surtout en carême. Siphons, bondes et joints sont ses portes d\'entrée favorites — la barrière et le traitement des points d\'eau règlent le problème.',
  },
  {
    question: 'Une scolopendre vue = une invasion ?',
    answer: 'Pas forcément, elle est solitaire. Mais si vous en voyez plusieurs par mois, votre terrain héberge une population et vos accès sont ouverts : c\'est le cas type où la barrière périmétrique change tout.',
  },
  {
    question: 'Le traitement tue aussi les ravets ?',
    answer: 'Oui, la barrière périmétrique est efficace contre la plupart des insectes rampants — beaucoup de clients traitent les deux d\'un coup (tarif groupé).',
  },
  {
    question: 'C\'est sans danger pour mes enfants et mon chien ?',
    answer: 'Le produit est appliqué à l\'extérieur et aux points d\'entrée, en respectant les délais de séchage indiqués par le technicien. Tout est expliqué avant l\'application.',
  },
];

export default function Scolopendres() {
  useDocumentHead({
    title: 'Scolopendres en Guadeloupe : traitement et prévention maison',
    description: 'La scolopendre (bête à mille pattes) entre chez vous ? Traitement et barrière préventive par nos techniciens, partout en Guadeloupe. Devis en 2 h.',
    jsonLd: [service({ name: 'Traitement anti-scolopendres', serviceType: 'Désinsectisation', path: '/scolopendres-guadeloupe', description: 'Traitement et barrière préventive contre les scolopendres en Guadeloupe.' }), faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Scolopendres', path: '/scolopendres-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Scolopendres'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/scolopendres-1.webp"
            alt="Technicien appliquant une barrière de protection anti-scolopendres autour d'une maison créole en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Scolopendres en Guadeloupe : empêchez la bête à mille pattes d&rsquo;entrer chez vous
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              C&rsquo;est l&rsquo;une des rencontres les plus redoutées dans une maison guadeloupéenne : une scolopendre de 15 ou 20 cm qui surgit d&rsquo;une plinthe, d&rsquo;une serviette ou d&rsquo;une chaussure. Contrairement aux mille-pattes inoffensifs, la scolopendre mord, et sa morsure est très douloureuse — un vrai risque pour les enfants, les personnes allergiques et les animaux. Bonne nouvelle : une maison peut être efficacement protégée. Nos techniciens traitent les zones refuges et posent une barrière périmétrique qui bloque l&rsquo;intrusion.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* POURQUOI LES SCOLOPENDRES ENTRENT */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                  Pourquoi les scolopendres entrent dans les maisons
                </h2>
                <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                  <p>
                    La scolopendre chasse la nuit (cafards, araignées, petits lézards) et cherche le frais et l&rsquo;humidité le jour. Elle entre surtout : en carême, quand la sécheresse la pousse à chercher l&rsquo;humidité des salles de bain et cuisines ; en saison des pluies, quand les fortes pluies inondent ses abris extérieurs ; et après le débroussaillage ou des travaux qui détruisent ses refuges (tas de bois, pierres, feuilles mortes, compost).
                  </p>
                  <p>
                    Les rez-de-jardin, les maisons avec jardin tropical dense et les abords de ravines sont les plus exposés.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-72 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="/images/scolopendres-2.webp"
                    alt="Inspection des abords d'une maison créole pour localiser les refuges de scolopendres en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* MORSURE DE SCOLOPENDRE */}
      <section className="bg-background-100 py-12 md:py-16">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="bg-secondary-100 border-l-4 border-secondary-500 rounded-r-xl p-6 md:p-8">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                Morsure de scolopendre : ce qu&rsquo;il faut savoir
              </h3>
              <p className="text-foreground-700 text-base leading-relaxed mb-4">
                Douleur immédiate et intense (souvent comparée à une brûlure), rougeur, gonflement — parfois plusieurs jours. Chez l&rsquo;enfant, la personne âgée ou allergique, un avis médical s&rsquo;impose. Les gestes utiles : laver au savon, appliquer du chaud (la chaleur dégrade le venin) puis de la glace contre le gonflement, surveiller.
              </p>
              <Link
                to="/blogue/morsure-scolopendre-que-faire"
                className="inline-flex items-center gap-2 text-secondary-500 font-semibold text-sm hover:text-secondary-600 transition-colors cursor-pointer"
              >
                Notre guide complet morsure de scolopendre
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* NOTRE TRAITEMENT */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Notre traitement anti-scolopendres
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-12">
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  Inspection des zones refuges autour de la maison, traitement rémanent des points d&rsquo;entrée, barrière périmétrique qui neutralise les scolopendres avant qu&rsquo;elles n&rsquo;arrivent à votre porte — et en bonus, elle stoppe aussi cafards et fourmis.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/scolopendres-3.webp"
                    alt="Pulvérisation d'une barrière périmétrique anti-scolopendres dans un jardin tropical en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {STEPS.map((step) => (
                <div key={step.number} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className={`${step.icon} text-secondary-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <div className="w-7 h-7 mb-3 flex items-center justify-center rounded-full bg-primary-500 text-white font-heading font-bold text-xs">
                    {step.number}
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-8 max-w-3xl mx-auto">
              <div className="bg-secondary-100/50 rounded-xl p-5 border border-secondary-200/50 text-center">
                <p className="text-foreground-700 text-sm leading-relaxed">
                  <strong>Le traitement protège plusieurs mois</strong> ; les maisons très exposées optent pour 2 passages par an (avant carême, avant saison des pluies).
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs anti-scolopendres
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <PriceTable rows={PRICING_ROWS} />
              <div className="text-center mt-6">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur les scolopendres
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Dormez tranquille : on met votre maison sous barrière."
      />

      {/* MAILLAGE INTERNE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/blogue/morsure-scolopendre-que-faire"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Morsure de scolopendre : que faire
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Les gestes qui soulagent et quand consulter
              </span>
            </Link>
            <Link
              to="/traitement-ravets-cafards-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement ravets & cafards
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Tarif groupé : mêmes zones traitées d&rsquo;un coup
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Voir tous nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit
              </span>
            </Link>
            <Link
              to="/desinsectisation-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement désinsectisation
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Tous nos traitements contre les insectes
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="scolopendres" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}